import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import { isDateInWeek, todayKey } from '@/domain/dates';
import { filterPlans, groupSharedPlans } from '@/domain/plans';
import type { SharedGroupKey } from '@/domain/plans';
import { PlanChip } from '@/features/plans/PlanChip';
import styles from './SharedView.module.css';

const GROUP_ORDER: SharedGroupKey[] = ['today', 'this_week', 'later', 'no_date', 'completed'];

export function SharedView() {
  const { t } = useTranslation();
  const session = useAppStore((s) => s.session);
  const plans = useAppStore((s) => s.plans);
  const filters = useAppStore((s) => s.filters);
  const preferences = useAppStore((s) => s.preferences);

  const viewerId = session?.user.id ?? '';
  const today = todayKey();
  const weekStartsOn = preferences?.weekStartsOn ?? 1;

  const visiblePlans = useMemo(() => {
    if (!viewerId) return [];
    return filterPlans(plans.filter((p) => !p.deletedAt), filters, viewerId);
  }, [plans, filters, viewerId]);

  const grouped = useMemo(
    () =>
      groupSharedPlans(visiblePlans, today, (dateKey) =>
        isDateInWeek(dateKey, new Date(), weekStartsOn),
      ),
    [visiblePlans, today, weekStartsOn],
  );

  const groupLabels: Record<SharedGroupKey, string> = {
    today: t('nav.today'),
    this_week: t('app.thisWeek'),
    later: t('app.later'),
    no_date: t('app.noDate'),
    completed: t('app.filters.completed'),
  };

  const hasAny = GROUP_ORDER.some((key) => grouped[key].length > 0);

  if (!hasAny) {
    return (
      <div className={styles.view}>
        <p className={styles.emptyRow}>{t('common.empty')}</p>
      </div>
    );
  }

  return (
    <div className={styles.view}>
      {GROUP_ORDER.map((key) => {
        const items = grouped[key];
        if (items.length === 0) return null;

        return (
          <section key={key} className={styles.group}>
            <h2 className={styles.groupTitle}>{groupLabels[key]}</h2>
            <div className={styles.rows}>
              {items.map((plan) => (
                <PlanChip key={plan.id} plan={plan} compact />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
